export type PaginationControl = "first" | "prev" | "next" | "last";

/** Class names rendered on the pagination buttons, used to find them again after a page change. */
export const paginationButtonClasses: Record<PaginationControl, string> = {
  first: "first-page-button",
  prev: "previous-page-button",
  next: "next-page-button",
  last: "last-page-button",
};

/** Order in which focus is handed over when the activated button can't take it back
 * (i.e. it got disabled on the new page or is not rendered anymore). */
const focusOrder: Record<PaginationControl, PaginationControl[]> = {
  first: ["first", "prev", "next", "last"],
  prev: ["prev", "first", "next", "last"],
  next: ["next", "last", "prev", "first"],
  last: ["last", "next", "prev", "first"],
};

/** A page change that never finishes (failed fetch, user navigated away) must not move the
 * focus minutes later, when the user is long gone from that table. */
const MAX_PENDING_MS = 60000;

/** Kept in module scope on purpose: most pages replace the whole table with a spinner while
 * the next page is fetched, so component state/refs don't survive until the focus is restored. */
let pendingFocus: {
  tableKey: string;
  control: PaginationControl;
  expiresAt: number;
} | null = null;

export const rememberPaginationFocus = (
  tableKey: string,
  control: PaginationControl,
) => {
  pendingFocus = {
    tableKey,
    control,
    expiresAt: Date.now() + MAX_PENDING_MS,
  };
};

const focusBack = (button: HTMLButtonElement) => {
  button.dataset.focusRestored = "true";
  button.addEventListener(
    "blur",
    () => {
      delete button.dataset.focusRestored;
    },
    { once: true },
  );
  button.focus();
};

const paginationButtonSelector = Object.values(paginationButtonClasses)
  .map((className) => `.${className}`)
  .join(",");

export const restorePaginationFocus = (
  container: HTMLElement | null,
  tableKey: string,
) => {
  if (!container || pendingFocus?.tableKey !== tableKey) {
    return;
  }

  if (Date.now() > pendingFocus.expiresAt) {
    pendingFocus = null;

    return;
  }

  const activeElement = document.activeElement;
  const focusWasDropped =
    !activeElement ||
    activeElement === document.body ||
    activeElement === document.documentElement;

  if (!focusWasDropped) {
    if (
      !container.contains(activeElement) ||
      !activeElement.closest(paginationButtonSelector)
    ) {
      pendingFocus = null;
    }

    return;
  }

  const { control } = pendingFocus;
  pendingFocus = null;

  const button = focusOrder[control]
    .map((item) =>
      container.querySelector<HTMLButtonElement>(
        `.${paginationButtonClasses[item]}`,
      ),
    )
    .find((item): item is HTMLButtonElement => !!item && !item.disabled);

  if (button) {
    focusBack(button);
  }
};
